import {
  Box,
  Button,
  type SortDirection,
  Table,
  theme,
} from '@factorialco/deprecated-design-system'
import { SoftwareCardSoftwareSetupFragment } from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import createComputed from 'modules/core/hooks/createComputed'
import usePagination from 'modules/core/hooks/usePagination'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import SoftwareCard from 'modules/software_management/components/softwareByEmployee/SoftwareCard'
import SoftwareList from 'modules/software_management/components/softwareByEmployee/SoftwareList'
import { UnionSoftwareType, getSoftware } from 'modules/software_management/lib/software'

interface OtherSoftwareListProps {
  employeeId: string
  softwareSetups: SoftwareCardSoftwareSetupFragment[]
  createEmployeeMembership: (employeeId: string, software: UnionSoftwareType) => void
  loadingSoftwareSetups: boolean
  currency: string
}

const useSortSoftwares = createComputed(
  (softwareSetups: SoftwareCardSoftwareSetupFragment[], direction: SortDirection) => {
    return orderBy(
      softwareSetups,
      (softwareSetup) => {
        const software = getSoftware(softwareSetup)
        return software.name
      },
      [direction]
    )
  }
)

const useSearchFilter = createComputed(
  (search: string, softwareSetups: SoftwareCardSoftwareSetupFragment[]) => {
    return softwareSetups.filter((softwareSetup) => {
      const software = getSoftware(softwareSetup)
      return matchText(search, software.name)
    })
  }
)

const OtherSoftwareList = ({
  softwareSetups,
  employeeId,
  createEmployeeMembership,
  loadingSoftwareSetups,
  currency,
}: OtherSoftwareListProps) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const sortedSoftwareSetups = useSortSoftwares(softwareSetups, 'asc').get()
  const filteredSoftwares = useSearchFilter(debouncedSearch, sortedSoftwareSetups).get()

  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: 20,
    items: filteredSoftwares,
  })

  const handleClick = (employeeId: string, software: UnionSoftwareType) => {
    createEmployeeMembership(String(employeeId), software)
  }

  return (
    <SoftwareList
      title={i18n.t('software_management.employee_software.detail.other_software.title')}
      subtitle={i18n.t('software_management.employee_software.detail.other_software.subtitle')}
      icon="task-list-search"
      hasMore={hasMore}
      loadMore={loadMore}
      onSearch={onSearch}
      page={page}
      paginatedItems={paginatedItems}
      search={search}
    >
      {paginatedItems.map((softwareSetup) => {
        const software = getSoftware(softwareSetup)
        return (
          <Table.Row key={softwareSetup.id}>
            <Table.Cell colSpan={1} noBorder wide padding="s0">
              {software && (
                <Box style={{ marginBottom: theme.dimensions.s24 }}>
                  <SoftwareCard
                    software={software}
                    softwareSetup={softwareSetup}
                    currency={currency}
                  >
                    <Button
                      disabled={loadingSoftwareSetups}
                      onClick={() => handleClick(employeeId, software)}
                      type="secondary"
                      iconName="add"
                    >
                      {i18n.t('software_management.employee_software.detail.add_application')}
                    </Button>
                  </SoftwareCard>
                </Box>
              )}
            </Table.Cell>
          </Table.Row>
        )
      })}
    </SoftwareList>
  )
}

export default OtherSoftwareList
